import { ScreenHelp } from "@/components/Help";
import type { ScreenId } from "@/components/Shell";
import { ErrorState, LoadingState } from "@/components/States";
import { BarTrend, Donut, DonutMulti, HBars, StatusBar } from "@/components/charts";
import { Card, Icon, StatusBadge, TableScroll } from "@/components/ui";
import {
  type Customer,
  MARGIN_FLOOR,
  type Quote,
  type QuoteTotals,
  STATUS,
  type StatusKey,
  customerNameFrom,
  fmt,
  fmtComp,
  isoDate,
  quoteTotals,
} from "@/lib/data";
import { useCustomers, useMonthly, useQuotes } from "@/lib/hooks";
import { cols, useIsMobile } from "@/lib/useViewport";
import {
  MAJOR_PROJECT_VOLUME,
  SUBMITTED_DATE,
  type SubmissionRow,
  countsByPurpose,
  countsPerWeek,
  customerShareForWeek,
  isSubmitted,
  rowsForWeek,
  submittedInRange,
  weeksInRange,
} from "@/lib/weeks";
import { useMemo, useState } from "react";

const DASHBOARD_HELP = [
  "견적 제출 현황에서 조회 기간(날짜)을 지정해 제출 건수 · 제출용도 · 고객사별 비중을 봅니다.",
  "월별 견적 추이 / 수주율 도넛 / 고객사별 견적 금액 / 상태 분포 차트로 현황을 한눈에 봅니다.",
  "'최근 견적' 행을 클릭하면 해당 견적 상세로 이동하고, '전체 보기' 로 견적 목록으로 이동합니다.",
  "'확인 필요' 패널은 최소 마진 미달 견적을 자동으로 모아 보여줍니다 — 클릭 시 상세로 이동.",
];

const RecentRow = ({
  q,
  cust,
  onClick,
}: {
  q: Quote & { t: QuoteTotals };
  cust: string;
  onClick: () => void;
}) => {
  const [h, setH] = useState(false);
  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        cursor: "pointer",
        background: h ? "var(--bg-surface-2)" : "transparent",
        borderTop: "1px solid var(--table-border)",
      }}
    >
      <td
        style={{
          padding: "13px 24px",
          fontFamily: "ui-monospace, monospace",
          fontSize: 12.5,
          color: "var(--text-near)",
        }}
      >
        {q.id}
      </td>
      <td style={{ padding: "13px 12px" }}>
        <div style={{ fontWeight: 700 }}>{cust}</div>
        <div
          style={{
            fontSize: 12,
            color: "var(--text-dim)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 240,
          }}
        >
          {q.project}
        </div>
      </td>
      <td
        style={{
          padding: "13px 12px",
          textAlign: "right",
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmt(q.t.submit)}
      </td>
      <td style={{ padding: "13px 24px", textAlign: "center" }}>
        <StatusBadge status={q.status} size="sm" />
      </td>
    </tr>
  );
};

// 도넛 세그먼트 색상 — 고객사별. 6색 순환(고객사 수가 더 많으면 반복).
const DONUT_COLORS = [
  "var(--brand)",
  "color-mix(in srgb, var(--brand) 55%, var(--bg-surface))",
  "var(--num-positive)",
  "var(--text-warning)",
  "var(--text-announcement)",
  "var(--text-negative)",
];

const PURPOSES = ["업체선정용", "RFQ용", "재견적", "샘플견적"];
// 제출용도별 색상 — 요약 칩·표 배지 공용(목업 색상 매핑).
const PURPOSE_COLOR: Record<string, string> = {
  업체선정용: "var(--num-positive)",
  RFQ용: "var(--brand)",
  재견적: "var(--text-warning)",
  샘플견적: "var(--accent-purple)", // RFQ용(파랑)과 구분되게 보라(수정요청)
};
const purposeColor = (p: string) => PURPOSE_COLOR[p] ?? "var(--text-dim)";

const PurposeBadge = ({ purpose }: { purpose: string }) => {
  const c = purposeColor(purpose);
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 9999,
        fontSize: 12,
        fontWeight: 700,
        color: c,
        background: `color-mix(in srgb, ${c} 14%, transparent)`,
        whiteSpace: "nowrap",
      }}
    >
      {purpose}
    </span>
  );
};

type SubSortKey = "customer" | "annualVolume" | "submittedAt" | "purpose";

// 견적 제출 현황 — 주차 단위 제출 추이/고객사 비중/프로젝트 표.
// 집계는 모두 lib/weeks 의 순수 함수에 위임, 여기선 상태·배치만.
const SubmissionStatus = ({
  quotes,
  customers,
  today,
  onOpenQuote,
}: {
  quotes: Quote[];
  customers: Customer[];
  today: string;
  onOpenQuote: (id: string) => void;
}) => {
  const mobile = useIsMobile();
  // 데이터에 존재하는 제출 견적의 날짜 범위(가장 이른~가장 늦은). 없으면 오늘.
  const submittedDates = quotes.filter(isSubmitted).map(SUBMITTED_DATE).sort();
  const minIso = submittedDates[0] ?? today;
  const maxIso = submittedDates[submittedDates.length - 1] ?? today;

  // 조회 기간 — 주차 대신 일 단위 날짜로 지정(수정요청: "7월 6일~7월 10일" 식 조회).
  const [startIso, setStartIso] = useState(minIso);
  const [endIso, setEndIso] = useState(maxIso);
  const [majorOnly, setMajorOnly] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [sort, setSort] = useState<{ k: SubSortKey; dir: "asc" | "desc" }>({
    k: "submittedAt",
    dir: "desc",
  });

  // 입력이 비었으면 데이터 범위로 보정, start>end 면 swap — 빈 화면 방지.
  const s = startIso || minIso;
  const e = endIso || maxIso;
  const lo = s <= e ? s : e;
  const hi = s <= e ? e : s;
  const rangeWeeks = useMemo(
    () => weeksInRange(new Date(`${lo}T00:00`), new Date(`${hi}T00:00`)),
    [lo, hi]
  );

  const submitted = submittedInRange(quotes, lo, hi, majorOnly);
  const perWeek = countsPerWeek(submitted, rangeWeeks);
  const purposeCounts = countsByPurpose(submitted);
  const distinctCust = new Set(submitted.map((q) => q.customerId)).size;

  // 선택 주차 — 기본은 범위의 마지막 주. 범위 밖이면 보정.
  const selected =
    rangeWeeks.find((w) => w.key === selectedKey) ?? rangeWeeks[rangeWeeks.length - 1];
  const selectedKeyEff = selected?.key ?? "";

  // 고객사 전부 표기(기타 묶음 없음 — 수정요청) · 조회 기간 밖 제출 제외(submitted 기준).
  const shares = customerShareForWeek(submitted, customers, selectedKeyEff, majorOnly);
  const shareTotal = shares.reduce((a, s) => a + s.count, 0);
  const shareColor = (i: number) => DONUT_COLORS[i % DONUT_COLORS.length];

  const rows = rowsForWeek(submitted, customers, selectedKeyEff, majorOnly);
  const sortedRows = [...rows].sort((a, b) => {
    const dir = sort.dir === "asc" ? 1 : -1;
    const av: string | number = a[sort.k];
    const bv: string | number = b[sort.k];
    return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
  });
  const setSortK = (k: SubSortKey) =>
    setSort((s) => ({ k, dir: s.k === k && s.dir === "desc" ? "asc" : "desc" }));

  // ponytail: x축 라벨에서 연도 제거(목업 "6월1주" 형태). 같은 월·주가 다른 연도에
  // 동시 표시되면 BarTrend 의 key(d.m) 가 충돌 — 제출 범위가 수년을 걸치면 라벨에 연도 복원.
  const barData = perWeek.map((p) => ({
    m: p.bucket.label.replace(/^\d+년 /, ""),
    quotes: p.count,
    won: 0, // 단일 시리즈 — 막대는 quotes 만 사용.
    amount: 0,
  }));
  const activeIndex = rangeWeeks.findIndex((w) => w.key === selectedKeyEff);

  const selectStyle: React.CSSProperties = {
    padding: "6px 10px",
    minHeight: mobile ? 40 : undefined,
    borderRadius: 6,
    border: "1px solid var(--table-border)",
    background: "var(--bg-surface)",
    color: "var(--text-base)",
    fontSize: 13,
  };
  const thStyle = (
    k?: SubSortKey,
    align: "left" | "right" | "center" = "left"
  ): React.CSSProperties => ({
    textAlign: align,
    padding: "10px 14px",
    fontWeight: 700,
    fontSize: 11.5,
    color: "var(--text-dim)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    cursor: k ? "pointer" : "default",
    whiteSpace: "nowrap",
    userSelect: "none",
  });

  return (
    <Card pad={24} style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 17 }}>
          견적 제출 현황
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="date"
            value={startIso}
            max={endIso || undefined}
            onChange={(ev) => setStartIso(ev.target.value)}
            style={selectStyle}
          />
          <span style={{ color: "var(--text-muted)" }}>~</span>
          <input
            type="date"
            value={endIso}
            min={startIso || undefined}
            onChange={(ev) => setEndIso(ev.target.value)}
            style={selectStyle}
          />
        </div>
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            color: "var(--text-near)",
            cursor: "pointer",
            marginLeft: "auto",
          }}
        >
          <input
            type="checkbox"
            checked={majorOnly}
            onChange={(e) => setMajorOnly(e.target.checked)}
          />
          주요 프로젝트만 보기
          <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
            (연평균 {fmt(MAJOR_PROJECT_VOLUME)} EA 이상)
          </span>
        </label>
      </div>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: cols(mobile, "1.6fr 1fr"), gap: 16 }}>
        <Card pad={20} style={{ background: "var(--bg-surface-2)" }}>
          <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>총 제출 건수</div>
          <div
            style={{
              fontFamily: "var(--font-title)",
              fontWeight: 900,
              fontSize: 30,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {submitted.length}
            <span style={{ fontSize: 16, fontWeight: 800 }}> 건</span>
          </div>
          {/* 제출용도 breakdown — 잘 보이게 확대(수정요청: 목업 스타일). */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(4, auto)",
              gap: "10px 26px",
              marginTop: 12,
              justifyContent: "start",
            }}
          >
            {PURPOSES.map((p) => (
              <span
                key={p}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 14.5,
                  fontWeight: 700,
                  color: "var(--text-near)",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: 3,
                    background: purposeColor(p),
                  }}
                />
                {p}
                <b
                  style={{
                    fontSize: 15.5,
                    color: "var(--text-base)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {purposeCounts[p] || 0}건
                </b>
              </span>
            ))}
          </div>
          <div
            style={{
              display: "inline-block",
              fontSize: 12.5,
              fontWeight: 700,
              color: "var(--brand)",
              background: "var(--brand-soft)",
              padding: "6px 12px",
              borderRadius: 6,
              marginTop: 12,
            }}
          >
            ※ 상기 4가지 제출용도의 합계는 총{" "}
            {PURPOSES.reduce((a, p) => a + (purposeCounts[p] || 0), 0)}
            건입니다.
          </div>
        </Card>
        <Card pad={20} style={{ background: "var(--bg-surface-2)" }}>
          <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>고객사 수</div>
          <div
            style={{
              fontFamily: "var(--font-title)",
              fontWeight: 900,
              fontSize: 30,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {distinctCust}
            <span style={{ fontSize: 16, fontWeight: 800 }}> 개사</span>
          </div>
        </Card>
      </div>

      {/* Bar trend (clickable) + donut */}
      <div style={{ display: "grid", gridTemplateColumns: cols(mobile, "1.7fr 1fr"), gap: 16 }}>
        <Card pad={20}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 16 }}>
            주차별 제출 건수 추이
          </div>
          {rangeWeeks.length ? (
            <BarTrend
              data={barData}
              height={200}
              activeIndex={activeIndex < 0 ? undefined : activeIndex}
              onBarClick={(i) => setSelectedKey(rangeWeeks[i].key)}
            />
          ) : (
            <div style={{ color: "var(--text-faint)", fontSize: 13 }}>표시할 주차가 없습니다</div>
          )}
        </Card>
        <Card pad={20} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 15, alignSelf: "flex-start", marginBottom: 4 }}>
            선택한 주차의 고객사별 제출 비중
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--text-faint)",
              alignSelf: "flex-start",
              marginBottom: 14,
            }}
          >
            {selected?.label ?? "—"}
          </div>
          {shareTotal ? (
            <>
              <DonutMulti
                segments={shares.map((s, i) => ({
                  label: s.name,
                  value: s.count,
                  color: shareColor(i),
                }))}
                centerLabel={`${shareTotal}건`}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  marginTop: 18,
                  width: "100%",
                }}
              >
                {shares.map((s, i) => (
                  <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: 2,
                        background: shareColor(i),
                      }}
                    />
                    <span style={{ fontSize: 13, color: "var(--text-near)", flex: 1 }}>
                      {s.name}
                    </span>
                    <span
                      style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                    >
                      {s.count}건
                    </span>
                    <span
                      style={{
                        fontSize: 11.5,
                        color: "var(--text-faint)",
                        width: 44,
                        textAlign: "right",
                      }}
                    >
                      {((s.count / shareTotal) * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ color: "var(--text-faint)", fontSize: 13, padding: "30px 0" }}>
              선택한 주차에 제출 건이 없습니다
            </div>
          )}
        </Card>
      </div>

      {/* Table */}
      <Card pad={0} style={{ overflow: "hidden" }}>
        <div style={{ fontWeight: 800, fontSize: 15, padding: "18px 18px 10px" }}>
          선택한 주차의 프로젝트 제출 현황{" "}
          <span style={{ fontSize: 12, color: "var(--text-faint)", fontWeight: 400 }}>
            {selected?.label ?? "—"}
          </span>
        </div>
        <TableScroll>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={thStyle(undefined, "center")}>NO</th>
                <th style={thStyle("customer")} onClick={() => setSortK("customer")}>
                  고객사명 {sort.k === "customer" && (sort.dir === "asc" ? "▲" : "▼")}
                </th>
                <th style={thStyle()}>프로젝트명</th>
                <th
                  style={thStyle("annualVolume", "right")}
                  onClick={() => setSortK("annualVolume")}
                >
                  연평균 물량(EA) {sort.k === "annualVolume" && (sort.dir === "asc" ? "▲" : "▼")}
                </th>
                <th style={thStyle("purpose", "center")} onClick={() => setSortK("purpose")}>
                  제출용도 {sort.k === "purpose" && (sort.dir === "asc" ? "▲" : "▼")}
                </th>
                <th
                  style={thStyle("submittedAt", "center")}
                  onClick={() => setSortK("submittedAt")}
                >
                  제출일 {sort.k === "submittedAt" && (sort.dir === "asc" ? "▲" : "▼")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r: SubmissionRow, i) => (
                <tr
                  key={r.id}
                  onClick={() => onOpenQuote(r.id)}
                  style={{ cursor: "pointer", borderTop: "1px solid var(--table-border)" }}
                >
                  <td
                    style={{ padding: "11px 14px", textAlign: "center", color: "var(--text-dim)" }}
                  >
                    {i + 1}
                  </td>
                  <td style={{ padding: "11px 14px", fontWeight: 700 }}>
                    {r.major && <span style={{ color: "var(--text-warning)" }}>★ </span>}
                    {r.customer}
                  </td>
                  <td style={{ padding: "11px 14px", color: "var(--text-near)" }}>{r.project}</td>
                  <td
                    style={{
                      padding: "11px 14px",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {fmt(r.annualVolume)}
                  </td>
                  <td style={{ padding: "11px 14px", textAlign: "center" }}>
                    <PurposeBadge purpose={r.purpose} />
                  </td>
                  <td
                    style={{ padding: "11px 14px", textAlign: "center", color: "var(--text-near)" }}
                  >
                    {r.submittedAt}
                  </td>
                </tr>
              ))}
              {!sortedRows.length && (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: "26px 0",
                      textAlign: "center",
                      color: "var(--text-faint)",
                      fontSize: 13,
                    }}
                  >
                    선택한 주차에 제출 건이 없습니다
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableScroll>
      </Card>
    </Card>
  );
};

export const DashboardScreen = ({
  onOpenQuote,
  onNavigate,
}: {
  onOpenQuote: (id: string) => void;
  onNavigate: (id: ScreenId) => void;
}) => {
  const mobile = useIsMobile();
  const quotesRes = useQuotes();
  const customersRes = useCustomers();
  const monthlyRes = useMonthly();
  const customers = customersRes.data;

  // "오늘" 기준 — 렌더 시점 1회 생성, 계산은 lib 헬퍼 사용.
  const today = isoDate(new Date());

  const withTotals = quotesRes.data.map((q) => ({ ...q, t: quoteTotals(q.items) }));
  const decided = withTotals.filter((q) => q.status === "won" || q.status === "lost");
  const wonCount = decided.filter((q) => q.status === "won").length;
  const winRate = decided.length ? (wonCount / decided.length) * 100 : 0;

  const segs = (Object.keys(STATUS) as StatusKey[]).map((k) => ({
    key: k,
    label: STATUS[k].label,
    color: STATUS[k].color,
    value: withTotals.filter((q) => q.status === k).length,
  }));

  const byCust: Record<string, number> = {};
  for (const q of withTotals) {
    const name = customerNameFrom(customers, q.customerId);
    byCust[name] = (byCust[name] || 0) + q.t.submit;
  }
  const custRank = Object.entries(byCust)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  const recent = [...withTotals].sort((a, b) => b.updated.localeCompare(a.updated)).slice(0, 5);

  const alerts: { type: "margin"; q: Quote & { t: QuoteTotals }; text: string }[] = [];
  for (const q of withTotals) {
    if (q.t.profitRate < MARGIN_FLOOR && q.status !== "lost") {
      alerts.push({
        type: "margin",
        q,
        text: `영업이익률 ${q.t.profitRate.toFixed(1)}% — 최소 마진 미달`,
      });
    }
  }
  const loading = quotesRes.loading || monthlyRes.loading;
  const error = quotesRes.error || monthlyRes.error;
  if (loading) {
    return (
      <div style={{ padding: "24px 28px 56px" }}>
        <LoadingState />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: "24px 28px 56px" }}>
        <ErrorState
          error={error}
          onRetry={() => {
            void quotesRes.reload();
            void monthlyRes.reload();
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 28px 56px", display: "flex", flexDirection: "column", gap: 24 }}>
      <ScreenHelp items={DASHBOARD_HELP} />
      {/* 견적 제출 현황 — 날짜 범위 조회 (issue #105, KPI 타일 행은 수정요청으로 삭제) */}
      <SubmissionStatus
        quotes={quotesRes.data}
        customers={customers}
        today={today}
        onOpenQuote={onOpenQuote}
      />

      {/* Charts row */}
      <div style={{ display: "grid", gridTemplateColumns: cols(mobile, "1.7fr 1fr"), gap: 16 }}>
        <Card pad={24}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 20,
            }}
          >
            <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 17 }}>
              월별 견적 추이
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--text-muted)" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: "color-mix(in srgb, var(--brand) 30%, var(--bg-surface))",
                  }}
                />
                전체 견적
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{ width: 10, height: 10, borderRadius: 2, background: "var(--brand)" }}
                />
                수주
              </span>
            </div>
          </div>
          <BarTrend data={monthlyRes.data} height={210} />
        </Card>
        <Card pad={24} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div
            style={{
              fontFamily: "var(--font-title)",
              fontWeight: 800,
              fontSize: 17,
              alignSelf: "flex-start",
              marginBottom: 16,
            }}
          >
            수주율
          </div>
          <Donut
            value={winRate}
            label={`${winRate.toFixed(0)}%`}
            sub={`결정 ${decided.length}건 중 ${wonCount}건 수주`}
          />
          <div style={{ display: "flex", gap: 28, marginTop: 22 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontWeight: 800, fontSize: 20, color: "var(--num-positive)" }}>
                {wonCount}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-dim)" }}>수주 성공</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontWeight: 800, fontSize: 20, color: "var(--text-negative)" }}>
                {decided.length - wonCount}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-dim)" }}>수주 실패</div>
            </div>
          </div>
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: cols(mobile, "1fr 1fr"), gap: 16 }}>
        <Card pad={24}>
          <div
            style={{
              fontFamily: "var(--font-title)",
              fontWeight: 800,
              fontSize: 17,
              marginBottom: 20,
            }}
          >
            고객사별 견적 금액{" "}
            <span style={{ fontSize: 12, color: "var(--text-faint)", fontWeight: 400 }}>
              제출 기준
            </span>
          </div>
          <HBars data={custRank} fmt={fmtComp} />
        </Card>
        <Card pad={24}>
          <div
            style={{
              fontFamily: "var(--font-title)",
              fontWeight: 800,
              fontSize: 17,
              marginBottom: 20,
            }}
          >
            견적 상태 분포
          </div>
          <StatusBar segments={segs} />
        </Card>
      </div>

      {/* Recent + alerts */}
      <div style={{ display: "grid", gridTemplateColumns: cols(mobile, "1.7fr 1fr"), gap: 16 }}>
        <Card pad={0} style={{ overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "20px 24px 14px",
            }}
          >
            <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 17 }}>
              최근 견적
            </div>
            <span
              onClick={() => onNavigate("list")}
              style={{
                fontSize: 13,
                color: "var(--brand)",
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              전체 보기 <Icon name="chevR" size={14} />
            </span>
          </div>
          <TableScroll>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
              <thead>
                <tr
                  style={{
                    color: "var(--text-faint)",
                    fontSize: 11.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  <th style={{ textAlign: "left", padding: "8px 24px", fontWeight: 700 }}>
                    견적번호
                  </th>
                  <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 700 }}>
                    고객사 · 프로젝트
                  </th>
                  <th style={{ textAlign: "right", padding: "8px 12px", fontWeight: 700 }}>
                    제출금액
                  </th>
                  <th style={{ textAlign: "center", padding: "8px 24px", fontWeight: 700 }}>
                    상태
                  </th>
                </tr>
              </thead>
              <tbody>
                {recent.map((q) => (
                  <RecentRow
                    key={q.id}
                    q={q}
                    cust={customerNameFrom(customers, q.customerId)}
                    onClick={() => onOpenQuote(q.id)}
                  />
                ))}
              </tbody>
            </table>
          </TableScroll>
        </Card>

        <Card pad={24}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
            <Icon name="alert" size={18} color="var(--text-warning)" />
            <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 17 }}>
              확인 필요
            </div>
            <span
              style={{
                marginLeft: "auto",
                background: "rgba(255,164,43,0.15)",
                color: "var(--text-warning)",
                fontWeight: 700,
                fontSize: 12,
                padding: "2px 10px",
                borderRadius: 9999,
              }}
            >
              {alerts.length}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {alerts.map((a) => (
              <div
                key={`${a.type}-${a.q.id}`}
                onClick={() => onOpenQuote(a.q.id)}
                style={{
                  display: "flex",
                  gap: 12,
                  padding: "12px 14px",
                  background: "var(--bg-surface-2)",
                  borderRadius: 6,
                  cursor: "pointer",
                  borderLeft: "3px solid var(--text-negative)",
                }}
              >
                <Icon
                  name="alert"
                  size={16}
                  color="var(--text-negative)"
                  style={{ marginTop: 2 }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {a.q.id}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                    {a.text}
                  </div>
                </div>
              </div>
            ))}
            {!alerts.length && (
              <div
                style={{
                  fontSize: 13,
                  color: "var(--text-faint)",
                  padding: "20px 0",
                  textAlign: "center",
                }}
              >
                확인할 항목이 없습니다
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
